import type { EditorCommand } from '@atlaskit/editor-common/types/editor-command';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { Step } from '@atlaskit/editor-prosemirror/transform';
import { ACTION } from '@atlaskit/editor-common/analytics/types/enums';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics/analyticsPluginType';

import { fireOfflineStorageAnalyticsEvent, parseErrorMessages } from '../analytics.ts';
import type { EditorOfflineStoragePlugin } from '../editorOfflineStoragePluginType.ts';

import { editorOfflineStoragePluginKey } from './main.ts';

/**
 * Called during the 'restoring' state when there are no steps to apply
 * @returns A command to set the plugin status to 'ready'
 */
export function noStepsCommand(): EditorCommand {
	return ({ tr }) => {
		tr.setMeta('addToHistory', false);
		tr.setMeta(editorOfflineStoragePluginKey, {
			status: 'ready',
		});
		return tr;
	};
}

/**
 * @param steps Prosemirror steps to apply to the transaction
 * @returns A command to apply the given steps to the transaction, and set the plugin status to 'ready'
 */
export function applyStepsCommand(
	steps: Step[],
	api: PublicPluginAPI<[EditorOfflineStoragePlugin, AnalyticsPlugin]> | undefined,
): EditorCommand {
	return ({ tr }) => {
		const failedSteps: { step: Step; error: Error }[] = [];
		steps.forEach((step) => {
			try {
				tr.step(step);
			} catch (e) {
				// step failed to apply, ignore it and attempt to apply other steps
				failedSteps.push({ step, error: e });
			}
		});

		if (failedSteps.length > 0) {
			fireOfflineStorageAnalyticsEvent({
				api,
				action: ACTION.OFFLINE_STORAGE_FAILED_STEPS,
				errorInfo: parseErrorMessages(failedSteps.map((s) => s.error)),
				stepsCount: failedSteps.length,
			});
		}

		tr.setMeta('addToHistory', false);
		tr.setMeta(editorOfflineStoragePluginKey, {
			status: 'ready',
		});
		return tr;
	};
}

/**
 * @returns A command to set the plugin status to 'tooManyRetries'
 */
export function tooManyRetriesCommand(): EditorCommand {
	return ({ tr }) => {
		tr.setMeta('addToHistory', false);
		tr.setMeta(editorOfflineStoragePluginKey, {
			status: 'tooManyRetries',
		});
		return tr;
	};
}

/**
 * @returns A command to the inactive syncing state
 *
 * The inactive syncing state is used to tell the FullPageEditorComponent that the
 * editor in this tab is not the active tab, and is waiting for the active tab to sync.
 * The FullPageEditorComponent will disable editing in this tab while the editor is in this state.
 */
export function setInactiveSyncingCommand(inactiveSyncing: boolean): EditorCommand {
	return ({ tr }) => {
		tr.setMeta('addToHistory', false);
		tr.setMeta(editorOfflineStoragePluginKey, {
			inactiveSyncing,
		});
		return tr;
	};
}
